/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef } from 'react';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import { Button } from './Button';
import { DateTimePicker } from './DateTimePicker';

function CalendarIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDisplayDate(isoDate) {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

export function CustomDateRangePicker({
  startDate,
  endDate,
  onApply,
  onClear,
  placeholder = 'DD/MM/YYYY - DD/MM/YYYY',
  className = '',
  triggerClassName = '',
  iconSrc,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const ref = useRef(null);

  useMobileScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
    }
  }, [isOpen, startDate, endDate]);

  useOutsideClick(
    ref,
    (e) => {
      if (
        !e.target.closest('#crm-datetime-picker-portal') &&
        !e.target.closest('.react-datepicker-popper')
      ) {
        setIsOpen(false);
      }
    },
    isOpen,
  );

  const hasValue = Boolean(startDate && endDate);
  const display = hasValue
    ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
    : placeholder;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`flex items-center gap-2 h-11 px-4 rounded-xl border border-border-default bg-white text-sm font-medium ${hasValue ? 'text-text-primary' : 'text-text-muted'} focus:outline-none hover:border-border-active transition-colors whitespace-nowrap ${triggerClassName}`}
      >
        <span>{display}</span>
        {iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className="w-4 h-4 object-contain shrink-0"
          />
        ) : (
          <CalendarIcon className="w-4 h-4 text-text-muted" />
        )}
      </button>

      {isOpen && (
        <div
          data-overlay-container="true"
          style={{ overscrollBehavior: 'contain' }}
          className="absolute right-0 top-[calc(100%+8px)] z-30 w-[260px] bg-white border border-border-default rounded-xl shadow-2xl p-4 flex flex-col gap-3"
        >
          <div>
            <label className="text-xs text-text-secondary uppercase tracking-wider mb-1.5 block">
              From
            </label>
            <DateTimePicker
              selected={draftStart ? new Date(draftStart) : null}
              onChange={(date) => setDraftStart(date ? toIsoDate(date) : '')}
              mode="date"
              allowFutureDates={false}
              maxDate={draftEnd ? new Date(draftEnd) : null}
              placeholderText="Select start date"
              className="w-full text-sm bg-layer-2-sidebar border border-border-default rounded-input px-3 py-2 text-text-primary focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          <div>
            <label className="text-xs text-text-secondary uppercase tracking-wider mb-1.5 block">
              To
            </label>
            <DateTimePicker
              selected={draftEnd ? new Date(draftEnd) : null}
              onChange={(date) => setDraftEnd(date ? toIsoDate(date) : '')}
              mode="date"
              allowFutureDates={false}
              minDate={draftStart ? new Date(draftStart) : null}
              placeholderText="Select end date"
              className="w-full text-sm bg-layer-2-sidebar border border-border-default rounded-input px-3 py-2 text-text-primary focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button
              variant="secondary"
              disabled={!draftStart && !draftEnd}
              onClick={() => {
                setDraftStart('');
                setDraftEnd('');
                onClear();
                setIsOpen(false);
              }}
              className="h-10 flex-1"
            >
              Clear
            </Button>
            <Button
              variant="primary"
              disabled={!draftStart || !draftEnd}
              onClick={() => {
                onApply(draftStart, draftEnd);
                setIsOpen(false);
              }}
              className="h-10 flex-1"
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
